/**
 * @format
 * @mixin Mixin to use with form components Shares some of the generic methods and logic
 */

import $csForm from '@/js/modules/csForm';
let validationDebounce;

export default {
	methods: {

		/**
		 * @public
		 * @description
		 * Gets the value of a field from a collection of form values
		 *
		 * @param {String} fieldId The field to search for
		 * @param {Array} collection The collection to search in
		 *
		 * @returns {Mixed}
		 */
		getFieldValueFromFieldCollection(fieldId, collection) {
			let fieldValue = null;

			for (let i = 0, field; (field = collection[i]); i++) {
				if (field.id === fieldId) {
					fieldValue = field.value;
					break;
				}
			}

			return fieldValue;
		},

		/**
		 * @public
		 * @description
		 * Gets the value for a field in the field values collection
		 *
		 * @param {String} fieldId The id of the field to find
		 * @param {Boolean} columnInRepeater Whether to look for the field in the current repeater item
		 * @param {Boolean} columnInParentRepeater Whether to look for the fiels in the current repeater item's parent repeater item
		 * @param {Object} currentRowValues The list of values of the current row to check
		 * @param {Object} parentRowValues The list of values of the parent of the current row to check
		 *
		 * @returns {Array|string} The fields value
		 */
		getValueForField(fieldId, columnInRepeater, columnInParentRepeater, isIgnoreNormalisation = false) {			
			try {
				let fieldValue = null;
				let isSubFormField = this.$_.isPlainObject(this.subFormSettings) && Object.keys(this.subFormSettings).length > 0;
				const isInternalLookup = this.field?.definition?.lookup?.isInternalLookup === true;

				if (columnInParentRepeater) {
					let repeater = this.formClass.getFieldInFormByGuid(this.field.repeaterGuid);
					fieldValue = fieldValue = this.formClass.getValueForFieldById(this.field, fieldId, repeater.repeaterGuid, repeater.fieldKeyIndex, 'value', isIgnoreNormalisation);
				} else if (columnInRepeater || (isSubFormField && !isInternalLookup)) {
					fieldValue = fieldValue = this.formClass.getValueForFieldById(this.field, fieldId, this.field.repeaterGuid, this.field.fieldKeyIndex, 'value', isIgnoreNormalisation);
				} else {
					fieldValue = this.formClass.getValueForFieldById(this.field, fieldId, undefined, undefined, 'value', isIgnoreNormalisation);
				}

				// If the value is stringify version of the boolean we convert it back to be the boolean here
				if (typeof fieldValue === 'string' && (fieldValue.toLowerCase() === 'true' || fieldValue.toLowerCase() === 'false')) {
					fieldValue = fieldValue.toLowerCase() == 'true';
				}

				return fieldValue;
			} catch (error) {
				this.$recordError({
					type: 'error',
					error: error,
					displayToUser: true,
					errorCode: '000096'
				});
			}
		},

		/**
		 * @public
		 * @description
		 * Gets the value for a field from the lookup field is associated with
		 *
		 * @param { Object } field The field object
		 * @param { Array } lookups The array of lookups fields
		 *
		 * @returns { String } The value of the lookup field
		 */
		getLookupValueForField(field, lookups) {
			let lookupValue = null;

			const lookupDisplayFieldTemplateParts = field.definition.lookup.displayFieldTemplateParts;
			const lookupDisplayFields = field.definition.lookup.displayFields;
			const configLookupsFields = lookups[field.definition.lookup.collectionId];

			// Use new format if available
			if (Array.isArray(lookupDisplayFieldTemplateParts) && lookupDisplayFieldTemplateParts.length > 0) {
				for (let p = 0, pLen = lookupDisplayFieldTemplateParts.length; p < pLen; p++) {
					let templatePart = lookupDisplayFieldTemplateParts[p];
					if (templatePart && templatePart.fieldId && Array.isArray(configLookupsFields) && configLookupsFields.length > 0) {
						for (let j = 0, lookupField; (lookupField = configLookupsFields[j]); j++) {
							if (lookupField.value === field.value) {
								lookupValue = lookupField[templatePart.fieldId];
							}
						}
					}
				}
			// Use old format
			} else if (Array.isArray(lookupDisplayFields) && lookupDisplayFields.length > 0) {
				for (let i = 0, valueToDisplay; (valueToDisplay = lookupDisplayFields[i]); i++) {
					if (Array.isArray(configLookupsFields) && configLookupsFields.length > 0) {
						for (let j = 0, lookupField; (lookupField = configLookupsFields[j]); j++) {
							if (lookupField.value === field.value) {
								lookupValue = lookupField[valueToDisplay];
							}
						}
					}
				}
			}

			return lookupValue;
		},

		/**
		 * @public
		 * @description
		 * Returns the correct classes for styling and aligning a button
		 * If method receive an array of buttons it will takes styles from the first available item from an array
		 *
		 * @returns { String }
		 */
		getButtonClasses(button) {
			const buttonObject = Array.isArray(button) ? button[0] : button;

			let arrayOfClassNames = ['cs-bg-light'];

			if (buttonObject) {
				if (buttonObject.style) {
					arrayOfClassNames = buttonObject.style.split(' ');
				}

				if (buttonObject.align) {
					arrayOfClassNames.push('float-' + buttonObject.align);
				}
			}

			return arrayOfClassNames.join(' ');
		},

		/**
		 * @public
		 * @description
		 * Transforms a standard form payload to the correct format for saving
		 *
		 * @param {Object} rawPayload The raw payload object for transformation
		 *
		 * @returns {Object} The transformed payload
		 */
		transformStandardPayload(rawPayload) {
			try {
				if (!rawPayload.fields) {
					throw new Error();
				}

				let payload = {};

				// map fields to object
				for (let i = 0, field; (field = rawPayload.fields[i]); i++) {
					const fieldPath = field.id.split('.');
					const fieldPathLength = fieldPath.length;

					let fieldPathSegment = '';
					let payloadItem = payload;

					for (let fp = 0; fp < fieldPathLength - 1; fp++) {
						fieldPathSegment = fieldPath[fp];
						if (!payloadItem[fieldPathSegment]) {
							payloadItem[fieldPathSegment] = {};
						}
						payloadItem = payloadItem[fieldPathSegment];
					}

					switch (field.fieldType) {
						case 'cs-repeater':
						case 'cs-checkbox':
						case 'cs-radio':
							payloadItem[fieldPath[fieldPathLength - 1]] = field.value;
							break;
						case 'cs-attachment':
							if (Array.isArray(field.filesToUpload)) {
								// We're hardcoding key here for a reference array of uploaded files for the root object
								if ( Array.isArray(payloadItem['filesToUpload']) ) {
									payloadItem['filesToUpload'].push(...field.filesToUpload);
								}
								else {
									payloadItem['filesToUpload'] = field.filesToUpload;
								}
							} else {
								payloadItem[fieldPath[fieldPathLength - 1]] = field.value;
							}
							break;
						case 'cs-select':
							if (field.componentSettings && field.componentSettings.multiple) {
								payloadItem[fieldPath[fieldPathLength - 1]] = field.value;
							} else {
								payloadItem[fieldPath[fieldPathLength - 1]] = Array.isArray(field.value) ? this.$_.head(field.value) : (field.value) ? field.value.toString() : field.value;
							}
							break;
						case 'cs-switch':
							payloadItem[fieldPath[fieldPathLength - 1]] = field.value && field.value.toString().toLowerCase() === 'true';
							break;
						default:
							payloadItem[fieldPath[fieldPathLength - 1]] = Array.isArray(field.value) ? field.value.join(', ') : field.value;
							break;
					}
				}

				return payload;
			} catch (error) {
				this.$recordError({
					type: 'error',
					error: error,
					displayToUser: true,
					errorCode: '000136'
				});

				return null;
			}
		},

		/**
		 * @public
		 * @description
		 * Removes a field from the invalid field list
		 * for the form or a section when it becomes valid
		 *
		 * @param {Object} field The field to add or remove, storing id, row id, section id and errors
		 * @param {Array} collectionToSearch The collection to search in
		 * @param {Boolean} isValid whether to add or remove the field - isValid = true = removes
		 */
		addOrRemoveInvalidFieldInCollection(field, collectionToSearch, isValid) {
			try {
				let foundField;
				for (let i = 0, invalidField; (invalidField = collectionToSearch[i]); i++) {
					// the fieldIdRef is unique so we can use this reliably across the forms
					if (invalidField.fieldIdRef === field.fieldIdRef) {
						if (isValid) {
							// TODO: can we do this in a faster way?
							collectionToSearch.splice(i, 1);
						} else {
							foundField = invalidField;
						}
					}
				}
				if (!isValid && typeof foundField === 'undefined') {
					collectionToSearch.push(field);
				}
			} catch (error) {
				this.$recordError({
					type: 'error',
					error: error,
					displayToUser: true,
					errorCode: '000002'
				});
			}
		},
		
		/**
		 * @public
		 * @description
		 * Passes up a request to show a question level comments. Overrideable
		 * 
		 * @param {String} fieldId the field ID
		 * @param {String} fullKeyRef The jump to field id
		 * 
		 */
		questionLevelCommentSelected(fieldId, fullKeyRef) {
			/**
			 * @description
			 * Emits a request to show question level comments for this field
			 *
			 * @event question-level-comment-selected
			 * @type {String}
			 * @type {String}
			 */
			this.$emit('question-level-comment-selected', fieldId, fullKeyRef);
		}
	}
};
